import { CommonModule } from "@angular/common";
import { Component, ElementRef, forwardRef, OnInit, ViewChild } from "@angular/core";
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from "@angular/forms";

@Component({
    selector: 'dai-percent-input',
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PercentInputComponent),
            multi: true,
        }
    ],
    template: `
      <input
        [formControl]="amountControl"
        (input)="onInput($event)"
        (blur)="onBlur()"
        (focus)="onFocus()"
        type="text"
        inputmode="decimal"
        class="form-control"
        #inputElement>
    `
})
export class PercentInputComponent implements ControlValueAccessor, OnInit {
    amountControl = new FormControl('');
    isDisabled = false;
    onChange = (value: any) => { };
    onTouched = () => { };
    value = 0;

    @ViewChild('inputElement', { static: true }) inputElement!: ElementRef<HTMLInputElement>;

    private _value: string | null = '';

    ngOnInit() {
        this.amountControl.valueChanges.subscribe(value => {
            if (value !== this._value) {
                this._value = value;
                this.value = parseFloat(value || '0');
                this.onChange(this.value);
            }
        });
    }

    writeValue(value: any): void {
        this._value = value;
        if (value !== null && value !== undefined && value !== '') {
            const formatted = this.format(value);
            if (formatted !== this.amountControl.value) {
                this.amountControl.setValue(formatted, { emitEvent: false });
            }
        } else {
            this.amountControl.setValue('', { emitEvent: false });
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
        isDisabled ? this.amountControl.disable() : this.amountControl.enable();
    }

    onInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        let value = input.value.replace(/[^0-9.]/g, '');

        // Ensure only one decimal point exists
        const decimalParts = value.split('.');
        if (decimalParts.length > 2) {
            value = decimalParts[0] + '.' + decimalParts.slice(1).join('');
        }

        // Preserve cursor position
        const position = input.selectionStart;
        this.amountControl.setValue(value, { emitEvent: true });
        setTimeout(() => input.setSelectionRange(position, position), 0);
        this.onTouched();
    }

    onBlur(): void {
        this.onTouched();
        const value = this.amountControl.value;
        if (value && !isNaN(parseFloat(value))) {
            this.amountControl.setValue(this.format(value), { emitEvent: false });
        }
    }

    onFocus(): void {
        const value = this.amountControl.value;
        if (value) {
            const plain = value.replace(/[^0-9.]/g, '');
            this.amountControl.setValue(plain, { emitEvent: false });
        }
    }

    private format(value: string | number): string {
        const parsed = typeof value === 'number' ? value : parseFloat(value);
        if (isNaN(parsed)) {
            return '';
        }
        return `${parsed}%`;
    }
}
